import cx from "classnames";

import { useTranslation } from "@/core/i18n";
import { Checkbox } from "atoms/checkbox/Checkbox";
import WarningTriangleIcon from "atoms/icon/icons/warning_triangle.svg";
import { Radio } from "atoms/radio/Radio";

interface ModalSelectorProps {
  /** Specify title */
  title: string;
  /** Specify subtext label of this selector (example: "recommended") */
  subtitle?: string;
  /** Specify label next to title */
  label: React.ReactNode | null;
  /** Specify tag prefix to title */
  tag: React.ReactNode | null;
  /**
   * What this option is worth under the offer being chosen (e.g. "3 at 50% off · Save $123").
   * Rendered in the savings ramp under the price, so the terms sit on the row where the choice is
   * actually made. Omitted rather than zeroed when the option cannot be priced.
   */
  valueLine?: string;
  /** Specify description */
  description?: string | null;
  /** Specify default checked value */
  checked?: boolean;
  /** Specify onChange selector */
  onChange: (value: boolean) => void;
  /** Specify the type of selector, as default radio */
  type?: "radio" | "checkbox";
  disabled?: boolean;
  /** Specify if this is a variant modal */
  isVariantsModal?: boolean;
  /** Specify if this is a location type */
  isLocationType?: boolean;
  /** Specify warning icon to display */
  warningIcon?: React.ReactNode;
  /** Specify whether to show warning */
  showWarning?: boolean;
}

export const ModalSelector = ({
  title,
  subtitle,
  valueLine,
  label,
  tag,
  description,
  checked,
  onChange,
  type = "radio",
  disabled = false,
  isLocationType = false,
  showWarning = false,
}: ModalSelectorProps) => {
  const { t } = useTranslation();
  return (
    <div
      className={cx(
        isLocationType
          ? "mb-3 mt-1 rounded-xl border-2 border-gray-200 p-4 hover:border-brand-color"
          : "border-b border-background-two py-4",
        checked && isLocationType ? "border-brand-color" : "",
        // A tint so the row reads as unavailable before the helper text is read. The radio is
        // already disabled (and so out of the keyboard order); this is the visual half of it.
        disabled && !isLocationType ? "bg-one" : "",
      )}
      aria-label={t("modalSelector.ariaLabel")}
      aria-disabled={disabled || undefined}
      onClick={(e) => {
        if (disabled) {
          return;
        }
        // In checkbox mode the row body is a native <label> wrapping the input, so a
        // click on the label text also re-dispatches a synthetic click on the input.
        // Both bubble here, and toggling on each cancels the selection out. Let the
        // checkbox own activation for clicks inside the label; this handler only
        // covers the surrounding row padding.
        if (type === "checkbox" && (e.target as HTMLElement).closest("label")) {
          return;
        }
        onChange(!checked);
        e.stopPropagation();
      }}
    >
      <div
        className={cx(
          "flex items-center justify-between",
          disabled ? "cursor-not-allowed" : "cursor-pointer",
        )}
      >
        <div className="content-right -ml-2.5 flex gap-5 text-right text-primary">
          {type === "checkbox" && (
            <Checkbox
              label={title}
              subtitle={subtitle}
              checked={checked}
              onChange={onChange}
              disabled={disabled}
              textStyle="text-bold2"
            />
          )}
          {type === "radio" && (
            <div className="flex items-center">
              <Radio
                checked={checked}
                onChange={(value) => {
                  if (!disabled) {
                    onChange(value);
                  }
                }}
                name={title}
                disabled={disabled}
              />
              <div
                className={cx(
                  "flex flex-col text-left align-middle",
                  isLocationType ? "ml-3 w-full" : "ml-1.5 gap-2",
                )}
              >
                <div className="flex flex-col gap-1">
                  <div className="flex items-center justify-between">
                    <div
                      className={cx(
                        disabled ? "text-dark-grey" : "text-primary",
                        description ? "text-bold1" : "text-bold2",
                      )}
                    >
                      <div className="flex items-center gap-2">
                        {tag}
                        {title}
                      </div>
                    </div>
                  </div>
                  {label && (
                    <div
                      className={cx("text-body2", disabled ? "text-dark-grey" : "text-secondary")}
                    >
                      {label}
                    </div>
                  )}
                  {valueLine && (
                    <div className="text-body2 font-medium text-success-green">{valueLine}</div>
                  )}
                  {subtitle && (
                    <div
                      className={cx("text-body2", disabled ? "text-dark-grey" : "text-secondary")}
                    >
                      {subtitle}
                    </div>
                  )}
                </div>
                {description && (
                  <div className="content-right text-body2 text-secondary">
                    {isLocationType && showWarning ? (
                      <div className="flex items-center justify-between">
                        <div>{description}</div>
                        <div className="relative -top-6 ml-24">
                          <WarningTriangleIcon width="28" height="28" />
                        </div>
                      </div>
                    ) : (
                      description
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
